import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Stack, Paper } from '@mui/material'
import Page from '../../layout/Page'
import { useLocation, useSearchParams } from 'react-router-dom'
import RoleContext from '../../auth/RoleContext';
import UserContext from '../../auth/UserContext'
import CacheContext from '../../cache/CacheContext'
import { ROLES } from '../../auth/role';
import { deleteDraftResource, deletePublishedResource, getSiteSettings, getResourceLibraryFileById, getResourceLibraryFiles, getResourceLibraryFilesByCategoryBatch, getResourceLibraryFilesByTag, getResourceLibraryFilesForIdentity, getResourceLibraryFilesInStatus, publishResourceLibraryDraft } from '../data'
import ResourceCategoryList from './ResourceCategoryList'
import SubmitResourceDialog from './SubmitResourceDialog'
import TabPanel from '../../form/TabPanel'
import ConfirmDialog from '../../form/ConfirmDialog'
import EditResourceDialog from './EditResourceDialog'
import ResourceLibraryCommandBar from './ResourceLibraryCommandBar';
import { SAFETYRESOURCES } from '../../users/data/preferences';
import CategoriesListControls from './ResourceList/CategoriesListControls';
import { getSiteConfig } from '../../community/siteProvider';
// import FeatureFlagsContext from '../../config/FeatureFlagsContext';

export default function ResourceLibraryPage(props = {}) {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams({ view: 'all_resources'})
  const roleDetails = useContext(RoleContext)
  const cache = useContext(CacheContext)
  const [isLoading, setIsLoading] = useState(true)
  const [files, setFiles] = useState({})
  const [searching, setSearching] = useState(false)
  const [categories, setCategories] = useState([])
  const [viewCategories, setViewCategories] = useState(null)
  const [createResourceDialogOpen, setCreateResourceDialogOpen] = useState(false)
  const [editResourceDialogOpen, setEditResourceDialogOpen] = useState(false)
  const userContext = useContext(UserContext)
  const [searchScope, setSearchScope] = useState(null)
  
  const cacheContext = useContext(CacheContext)
  // const ff = useContext(FeatureFlagsContext)

  const { pathname } = useLocation()
  
  const siteKey = useMemo(() => {
    return pathname.split('/').filter(Boolean)[0] ?? null
  }, [pathname])
  
  const [config, setConfig] = useState(null)

  const TabbedViews = {
    ALL_RESOURCES: 'all_resources',
    MY_FAVORITES: 'my_favorites',
    MY_RESOURCES: 'my_resources',
    SBMTD_RESOURCES: 'submitted_resources',
    RESULTS: 'results'
  }

  const [currentView, setCurrentView] = useState(TabbedViews.ALL_RESOURCES)
  const [viewBump, setViewBump] = useState(0)
  const [resultView, setResultView] = useState(null)


  const getFavorites = async () => {
    const favs = await userContext.getFavorites(userContext.uniqueId, SAFETYRESOURCES)
    cache.setItem(`${SAFETYRESOURCES}Favorites`, favs)
    return favs
  }

  useEffect(() => {
    (async () => {
      const _config = getSiteConfig(siteKey)
      setConfig(_config)

      const settings = await getSiteSettings(true)
      const cats = settings.resources.categories
      cats.sort((a, b) => a.localeCompare(b))
      setCategories(cats)
      setViewCategories(cats)
      await getFavorites()
    })()
    }, [siteKey])

  const getCurrentView = () => {
    const resourceId = searchParams.get('resourceId') ?? null
    let currentView = searchParams.get('view') ?? TabbedViews.ALL_RESOURCES
    if (resourceId) {
      currentView = TabbedViews.RESULTS
    }
    return [currentView, resourceId]
  }

  useEffect(() => {
    const [_currentView, _resourceId] = getCurrentView()
    
    if (_currentView === TabbedViews.ALL_RESOURCES && categories.length > 0) {
        (async () => {
          getFavorites()
          setSearching(true)
          let _files = {}
          try {
            const categoryBatchResults = await getResourceLibraryFilesByCategoryBatch(categories)
            const categoryFiles = categoryBatchResults.map((batch) => {
              const category = batch.id
              const files = batch.body.value
              const pubs = files.reduce((p, file) => {
                if (file.driveItem.parentReference.name === 'Published') {
                  return [...p, file]
                }
                return p
              }, [])
              return { [category]: pubs}
            })
            _files = Object.assign({}, ...categoryFiles)

          } finally {
            setSearching(false)
          }
          setFiles(_files)
        })()
    }
    if (_currentView === TabbedViews.MY_FAVORITES) {
      (async () => {
        setSearching(true)
        const _files = {}
        try {
          const favs = await getFavorites()
          if (favs && favs.length) {
            const results = await getResourceLibraryFiles(favs, undefined, true)
            _files['Favorites'] = results
          }
        } finally {
          setSearching(false)
        }
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.MY_RESOURCES) {
      (async () => {
        setSearching(true)
        const _files = {}
        try {
          
          const requests = [
            getResourceLibraryFilesForIdentity({ email: userContext.email, userName: userContext.userName }, 'Submitted'),
            getResourceLibraryFilesForIdentity({ email: userContext.email, userName: userContext.userName }, 'Published'),
          ]
          
          const groups = await Promise.all(requests)
          _files['Submitted'] = groups[0]
          _files['Published'] = groups[1]

        } finally {
          setSearching(false)
        }
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.SBMTD_RESOURCES) {
      (async () => {
        const _files = {}
        try {
          _files['Submitted'] = await getResourceLibraryFilesInStatus('DRAFT')
        } finally {
          setSearching(false)
        }
        // console.log('_files', files)
        setFiles(_files)
      })()
    }

    if (_currentView === TabbedViews.RESULTS) {
      const _tagView = searchParams.get('tagged') ?? null

      if (_resourceId) {
        const resource = location.state ? location.state[_resourceId] : undefined
        
        if (resource) {
          const title = resource.fields?.Title
          const fileName = resource.fields?.LinkFilename
          const _files = { [title ?? fileName]: [resource]}
          setResultView(title ?? fileName)
          setFiles(_files)
        } else {
          // search by resourceId
          (async () => {
            setSearching(true)
            try {
              const resource = await getResourceLibraryFileById(_resourceId)
              const title = resource.fields?.Title
              const fileName = resource.fields?.LinkFilename
              setResultView(title ?? fileName)
              const _files = { [title ?? fileName]: [resource]}
              setFiles(_files)
            } finally {
              setSearching(false)
            }
          })()
        }
      } else if (_tagView) {
        (async () => {
          setSearching(true)
          setResultView(_tagView)
          const _files = { [_tagView]: []}
          try {
            const _taggedFiles = await getResourceLibraryFilesByTag(_tagView)

            _files[_tagView] = _taggedFiles.reduce((p, file) => {
              if (file.driveItem.parentReference?.name === 'Published') {
                return [...p, file]
              }
              return p
            }, [])
          } finally {
            setSearching(false)
          }
            
          // console.log('_files', _files)
          setFiles(_files)
        })()
      }
    }
    setCurrentView(_currentView)

  }, [categories, searchParams, viewBump])

  const startLoading = () => {
    setIsLoading(true)
  }

  const stopLoading = () => {
    setIsLoading(false)
  }

  const initializePage = async () => {
    startLoading()
    // 
    stopLoading()
  }

  useEffect(() => {
    initializePage()
  }, [])

  const [confirmDeleteDialogActive, setConfirmDeleteDialogActive] = useState(false)
  const [confirmDeleteAction, setConfirmDeleteAction] = useState({ resourceId: undefined, listKey: undefined, isPublished: false})

  const findResourceInList = (resourceId, listKey) => {
    const _tagView = searchParams.get('tagged') ?? null
    const [_currentView, _resourceId] = getCurrentView()

    let fileList
    if (_tagView !== null) {
      fileList = files[_tagView]
    } else if (_currentView === TabbedViews.RESULTS) {
      fileList = files[resultView]
    } else if (_currentView === TabbedViews.MY_FAVORITES) {
      fileList = files['Favorites']
    } else {
      fileList = files[listKey]
    }

    const listIndex = fileList.findIndex((f,i) => f.driveItem.id === resourceId)
    const _file = fileList[listIndex]
    return [_file, listIndex]
  }

  const onApproveDraft = useCallback(async (resourceId, listKey) => {
    const [_file, listIndex] = findResourceInList(resourceId, listKey)
    if (_file?.fields.Status === 'DRAFT') {
      await publishResourceLibraryDraft(resourceId)
    }
    setViewBump(viewBump + 1)
  })

  const onDeleteResource = useCallback(async (resourceId, listKey) => {
    const [_file, listIndex] = findResourceInList(resourceId, listKey)
    if (_file.fields.Status === 'DRAFT') {
      await deleteDraftResource(resourceId)
    } else {
      await deletePublishedResource(resourceId)
    }
    const _files = structuredClone(files)
    _files[listKey].splice(listIndex, 1)
    setFiles(_files)
  })

  const onSelectTag = useCallback((tag) => {
    setSearchParams({tagged: tag, view: TabbedViews.RESULTS})
  })

  const confirmDeleteResource = useCallback((resourceId, listKey) => {
    setConfirmDeleteAction({resourceId, listKey})
    setConfirmDeleteDialogActive(true)
  })

  const [editResourceProperties, setEditResourceProperties] = useState(null)
  const onEditResourceProperties = useCallback((resourceId, listKey) => {
    const [_file] = findResourceInList(resourceId, listKey)
    const fileName = _file.fields?.LinkFilename
    const title = _file.fields?.Title
    const description = _file.driveItem?.description
    const resourceCategory = _file.fields?.QSEPortalResourceCategory
    const resourceTags = _file.fields?.QSEPortalTags
    setEditResourceProperties({
      resourceId,
      category: resourceCategory,
      fileName,
      title,
      description,
      tags: resourceTags?.split(',') || undefined,
    })
    setEditResourceDialogOpen(true)
  }, [files])

  const onEditResourceCloseEvent = useCallback((navToMyResources, fileTouched) => {
    setEditResourceDialogOpen(false)
    setEditResourceProperties(null)
    if (navToMyResources && searchParams.get('view') !== TabbedViews.MY_RESOURCES) {
      setSearchParams({view: TabbedViews.MY_RESOURCES})
    } else if (fileTouched) {
      setViewBump(viewBump + 1)
    }
  }, [])

  
  const sortCategories = useCallback((isAscending = true) => {
    let sc = [...viewCategories]
    if (isAscending === true) {
      sc.sort((a, b) => a.localeCompare(b))
    } else if (isAscending === false) {
      sc.sort((a, b) => b.localeCompare(a))
    }
    setViewCategories(sc)
  }, [viewCategories])

  const filterCategories = useCallback((selection) => {
    if (selection.join('') === 'all') {
      setViewCategories(categories)
    } else {
      setViewCategories(selection)
    }
  })

  const [categoryExpand, setCategoryExpand] = useState(null)

  const tabViews = [
    { 
      key: TabbedViews.ALL_RESOURCES,
      label: 'All Resources', 
      title: 'Categorized lists of approved shared files',
      el: currentView === TabbedViews.ALL_RESOURCES && (
          <Stack gap={1}>
            <CategoriesListControls setCategoryExpand={setCategoryExpand} 
            sortCategories={sortCategories}
            categories={categories}
            filterCategories={filterCategories}
            viewCategories={viewCategories} />
            {/* {categoriesListControls()} */}
            {viewCategories && viewCategories.map((category, idx) => {
              if (!searching && viewCategories.length === categories.length && files?.[category]?.length === 0) {
                return <Fragment key={category} />
              } else {
                return <ResourceCategoryList key={category}
                  category={category}
                  categoriesList={categories}
                  files={files?.[category]}
                  working={searching}
                  onEditResourceProperties={onEditResourceProperties}
                  onDeleteDocument={confirmDeleteResource}
                  onSelectTag={onSelectTag}
                  supportResourceUpload={true}
                  onResourceUploaded={onEditResourceCloseEvent}
                  expand={categoryExpand ?? idx === 0}
                  onSetSearchScope={(scope) => setSearchScope(scope)}
                />
              }
            })
            }
        </Stack>
      )
    },
    { 
      key: TabbedViews.MY_FAVORITES,
      label: 'Favorites',
      title: 'Files you\`ve favorited',
      el: currentView === TabbedViews.MY_FAVORITES && (
        <Stack gap={1}>
          <CategoriesListControls setCategoryExpand={setCategoryExpand} 
            sortCategories={sortCategories}
            categories={categories}
            filterCategories={filterCategories}
            viewCategories={viewCategories}
            includeFilter={false}
            includeSort={false}
            />
          {/* {categoriesListControls(false, false)} */}
          <ResourceCategoryList key={'favorites'}
            category={'Favorites'}
            files={files?.['Favorites']}
            working={searching}
            aggregatedView={true}
            onEditResourceProperties={onEditResourceProperties}
            onDeleteDocument={confirmDeleteResource}
            onApproveDraft={onApproveDraft}
            onSelectTag={onSelectTag}
            expand={categoryExpand ?? true}
            onSetSearchScope={(scope) => setSearchScope(scope)}
          />
      </Stack>
      )
    },
    { 
      key: TabbedViews.MY_RESOURCES,
      label: 'My Resources',
      title: 'Files you\`ve submitted',
      el: currentView === TabbedViews.MY_RESOURCES && (
        <Stack gap={1}>
          <CategoriesListControls setCategoryExpand={setCategoryExpand} 
            sortCategories={sortCategories}
            categories={categories}
            filterCategories={filterCategories}
            viewCategories={viewCategories}
            includeFilter={false}
            includeSort={false}
            />
          {/* {categoriesListControls(false, false)} */}
          { ['Submitted', 'Published'].reduce((p, category, idx) => {
            if (files?.[category]?.length > 0) {
              return [...p, <ResourceCategoryList key={idx}
            category={category}
            files={files?.[category]}
            working={searching}
            onEditResourceProperties={onEditResourceProperties}
            onDeleteDocument={confirmDeleteResource}
            onApproveDraft={onApproveDraft}
            onSelectTag={onSelectTag}
            expand={categoryExpand ?? true}
          />]
            }
            return p
          }, [])}
      </Stack>
      )
    }
  ]
  if (roleDetails.hasRole([ROLES.QSE_SAFETY_MANAGER, ROLES.ADMIN])) {
    tabViews.push(
      { 
        key: TabbedViews.SBMTD_RESOURCES,
        label: 'Submitted Drafts', 
        title: 'Files that have been submitted but need approval to be visible to users',
        el: currentView === TabbedViews.SBMTD_RESOURCES && (
          <Stack gap={1}>
            { (files?.['Submitted']?.length > 0) &&
               <ResourceCategoryList key={'Submitted'}
              category={'Submitted'}
              files={files?.['Submitted']}
              working={searching}
              onEditResourceProperties={onEditResourceProperties}
              onDeleteDocument={confirmDeleteResource}
              onApproveDraft={onApproveDraft}
              suppressTags={true}
              suppressFavorites={true}
            />
            }
        </Stack>
        )
      }
    )
  }
  if (resultView !== null) {
    tabViews.push({
      key: TabbedViews.RESULTS,
      label: 'Results',
      el: currentView === TabbedViews.RESULTS && (
        <Stack gap={1}>
            <ResourceCategoryList key={resultView}
              category={resultView}
              files={files?.[resultView]}
              working={searching}
              filter={resultView}
              aggregatedView={true}
              onEditResourceProperties={onEditResourceProperties}
              onDeleteDocument={confirmDeleteResource}
              onClearFilter={() => {
                setResultView(null)
                setSearchParams({ view: TabbedViews.ALL_RESOURCES})}
              }
              onSelectTag={onSelectTag}
            />
          </Stack>
      )
    })
  }

  const onTabChange = (tabIndex) => {
    const viewKey = tabViews[tabIndex].key
    setResultView(null)
    setSearchParams({view: viewKey})
    setCategoryExpand(null)
  }

  const onResourceDialogClose = (navToMyResources, fileTouched = false) => {
    // console.log(navToMyResources, fileTouched)
    setCreateResourceDialogOpen(false)
    if (navToMyResources) {
      if ((searchParams.get('view') ?? '') === TabbedViews.MY_RESOURCES) {
        setViewBump(viewBump + 1)
      } else {
        setSearchParams({view: TabbedViews.MY_RESOURCES})
      }
    } else if (fileTouched) {
      setViewBump(viewBump + 1)
    }
  }


  return (
    <>
    <EditResourceDialog
          open={editResourceDialogOpen}
          resourceCategories={categories}
          editResourceProperties={editResourceProperties}
          onClose={onEditResourceCloseEvent}
    />
    <SubmitResourceDialog
          open={createResourceDialogOpen}
          resourceCategories={categories}
          onClose={onResourceDialogClose}
    />
    <ConfirmDialog open={confirmDeleteDialogActive}
      hideDialog={() => {setConfirmDeleteAction(undefined); setConfirmDeleteDialogActive(false)}}
      handleConfirm={() => {onDeleteResource(confirmDeleteAction.resourceId, confirmDeleteAction.listKey); setConfirmDeleteDialogActive(false)}}
      title='Are you sure you want to delete?'
      text='This item will be permanently deleted'
      yesLabel='Yes, Delete'
      noLabel='Cancel'
    />
    <Page {...props} breadcrumb={<div><span>{config?.label ?? ''} Resource Library</span></div>} isLoading={isLoading} commandBar={
      <ResourceLibraryCommandBar addRecord={() => setCreateResourceDialogOpen(true)}
        addRecordButtonVariant="contained"
        showAdd={!createResourceDialogOpen}
        addRecordLabel='Submit a Resource' 
        searchScope={searchScope}
        onClearSearchScope={() => setSearchScope(null)}/>
      }>
      <Paper
        sx={{ backgroundColor: 'transparent', display: 'flex', flexDirection: 'column', flexWrap: 'wrap', margin: '0 auto 1em auto', boxShadow: 'none' }}
      >
        <TabPanel panelId='resources' key='resources' tabs={tabViews} tabStyle={{ textTransform: 'none', minHeight: 'unset'}}
          defaultTab={Math.max(tabViews.findIndex( v => v.key === currentView), 0)}
          onTabChange={(idx) => onTabChange(idx)}
        />
      </Paper>
    </Page>
    </>
  )
}